// React
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

// Internal Components
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";

// External Packages
import { Helmet } from "react-helmet";
import axios from "axios";
import Swal from "sweetalert2";

// Styling
import "./contact.css";
import SEO from "../../components/seo/SEO";
import Honeypot from "../../components/ui/Honeypot/Honeypot";
import useFormToken from "../../hooks/useFormToken";
import {
  CONTACT_ENQUIRY_OPTIONS,
  CONTACT_LIMITS,
  HONEYPOT_FIELD,
} from "../../constants/contactOptions";

const API_URL = process.env.REACT_APP_API_URL;

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [enquiry, setEnquiry] = useState("");
  const [body, setBody] = useState("");
  const [sent, setSent] = useState(0);

  // Bot signals. Neither is visible to a real user: the honeypot is off-screen
  // and the token is fetched on mount. See middlewares/botCheck.js.
  const [honeypot, setHoneypot] = useState("");
  const formToken = useFormToken();

  const { type } = useParams();
  const navigate = useNavigate();
  const form = useRef();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  // Set default enquiry type if passed in URL
  useEffect(() => {
    if (type === "bug_found") {
      setEnquiry("bug_found");
    }
  }, [type]);

  const sendEmail = async (e) => {
    e.preventDefault();
    if (sent === 1) return; // guard against a double submit
    setSent(1);

    // Create a data object from the form state
    const formData = {
      name, // from useState
      email, // from useState
      enquiry, // from useState
      body, // from useState
      [HONEYPOT_FIELD]: honeypot, // empty for every real user
      formToken, // server-signed; proves how long the form was open
    };

    try {
      const { data } = await axios.post(`${API_URL}/api/send-email`, formData);

      if (data) {
        e.target.reset(); // Reset form fields
        setSent(2);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setSent(0);
      Swal.fire(
        "Error!",
        error.response?.data?.message ||
          "Something went wrong sending your message. Please try again or email us directly.",
        "error",
      );
    }
  };

  return (
    <>
      {userInfo ? <Header variant="light" /> : <Header variant="light" />}
      <SEO
        title="Contact Us"
        description="Please feel free to get in touch"
        canonicalUrl={`https://www.nowasteddays.io/contact`}
      />
      <main className="contact-page-main">
        {sent !== 2 ? (
          <div className="tile--email">
            <form
              id="contactForm"
              className="contact-form"
              ref={form}
              onSubmit={sendEmail}
            >
              {/* Name */}
              <div className="form-group">
                <div className="dateRow">
                  <label htmlFor="contactName">Name</label>
                </div>
                <div className="input-container">
                  <input
                    id="contactName"
                    className="formInput"
                    value={name}
                    name="from_name"
                    placeholder="Enter your name..."
                    onChange={(e) => setName(e.target.value)}
                    minLength={2}
                    maxLength={CONTACT_LIMITS.NAME_MAX}
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="form-group">
                <div className="dateRow">
                  <label htmlFor="email">Email</label>
                </div>
                <div className="input-container">
                  <input
                    id="email"
                    className="formInput"
                    type="email"
                    value={email}
                    name="from_email"
                    placeholder="Enter your email address..."
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={CONTACT_LIMITS.EMAIL_MAX}
                    required
                  />
                </div>
              </div>

              {/* Enquiry Type */}
              <div className="form-group">
                <div className="dateRow">
                  <label htmlFor="contactEnquirySelect">Enquiry Type</label>
                </div>
                <div className="input-container">
                  <select
                    id="contactEnquirySelect"
                    className="formInput"
                    name="enquiry_type"
                    value={enquiry}
                    onChange={(e) => setEnquiry(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Select an option...
                    </option>
                    {CONTACT_ENQUIRY_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div className="form-group">
                <div className="dateRow">
                  <label htmlFor="messageArea">Message</label>
                </div>
                <div className="input-container">
                  <textarea
                    id="messageArea"
                    className="formInput"
                    value={body}
                    name="message"
                    rows="8"
                    placeholder="Please provide as much detail as possible..."
                    onChange={(e) => setBody(e.target.value)}
                    minLength={CONTACT_LIMITS.BODY_MIN}
                    maxLength={CONTACT_LIMITS.BODY_MAX}
                    required
                  />
                </div>
              </div>

              <Honeypot value={honeypot} onChange={setHoneypot} />

              <button
                className="contact-submit-btn"
                type="submit"
                value="Send"
                disabled={sent === 1}
              >
                {sent === 0 && "Send Message"}
                {sent === 1 && "Sending Message..."}
              </button>
            </form>
          </div>
        ) : (
          <div className="tile--email">
            <h2 className="contact-header">
              Message Sent! Thank you for reaching out!
            </h2>
          </div>
        )}
      </main>
      <TestFooter />
    </>
  );
}
